import React, { useState, useEffect, useMemo } from "react";
import { useTableColumnFilters, FilterTh } from "../components/tableFilters";
import { useAuth } from "../context/AuthContext";
import { userService, workshopService } from "../services/dataService";
import { authService } from "../services/authService";
import { getRoles } from "../services/api";
import { User, Workshop } from "../types";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import "./UserManagement.css";

const roles = [
  { value: "Manager", label: "Müdür" },
  { value: "Sekreterya", label: "Sekreterya" },
  { value: "Modelist", label: "Modelist" },
  { value: "Üretim", label: "Üretim" },
  { value: "Muhasebe", label: "Muhasebe" },
];

// Rol rozetine kurumsal ton
const roleTints: Record<string, { bg: string; fg: string }> = {
  Manager: { bg: "#eff6ff", fg: "#1d4ed8" },
  Sekreterya: { bg: "#f5f3ff", fg: "#6d28d9" },
  Modelist: { bg: "#ecfeff", fg: "#0e7490" },
  Üretim: { bg: "#fff7ed", fg: "#b45309" },
  Muhasebe: { bg: "#f1f5f9", fg: "#334155" },
};

// Avatar rengi (kullanıcıya göre deterministik)
const avatarPalette = [
  "#2563eb",
  "#0e7490",
  "#7c3aed",
  "#b45309",
  "#0f766e",
  "#be185d",
  "#4338ca",
];
const pickAvatarColor = (seed: string) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return avatarPalette[h % avatarPalette.length];
};
const initials = (first: string, last: string) =>
  `${(first || "").charAt(0)}${(last || "").charAt(0)}`.toUpperCase() || "?";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  role: "",
  workshopIds: [] as string[],
  isActive: true,
  password: "",
};

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [apiRoles, setApiRoles] = useState<{ roleId: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });

  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    userId: string | null;
    userName: string;
  }>({ isOpen: false, userId: null, userName: "" });

  const isManager = currentUser?.role === "Manager";
  const isEdit = !!editingUser;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, workshopsData, rolesRes] = await Promise.all([
        userService.getAll(),
        workshopService.getAll(),
        getRoles(),
      ]);
      setUsers(usersData);
      setWorkshops(workshopsData);
      setApiRoles(rolesRes.data || []);
    } catch (error) {
      console.error("Error loading data:", error);
      setAlertModal({
        isOpen: true,
        message: "Veriler yüklenirken hata oluştu",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingUser(null);
    setFormData({ ...emptyForm });
    setIsModalOpen(true);
  };

  const openEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
      workshopIds: user.workshops?.map((w) => w.workshopId) || [],
      isActive: user.isActive,
      password: "",
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setFormData({ ...emptyForm });
  };

  const toggleWorkshop = (workshopId: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      workshopIds: checked
        ? [...prev.workshopIds, workshopId]
        : prev.workshopIds.filter((id) => id !== workshopId),
    }));
  };

  const toggleAllWorkshops = () => {
    setFormData((prev) => ({
      ...prev,
      workshopIds:
        prev.workshopIds.length === workshops.length
          ? []
          : workshops.map((w) => w.workshopId),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isEdit) {
      if (!editingUser) return;
      try {
        setSaving(true);
        const updateData: any = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          role: formData.role,
          isActive: formData.isActive,
        };
        if (formData.workshopIds.length > 0) {
          updateData.workshopIds = formData.workshopIds;
        }
        if (formData.password && formData.password.trim().length >= 6) {
          updateData.password = formData.password.trim();
        }
        await userService.update(editingUser.userId, updateData);
        setAlertModal({
          isOpen: true,
          message: "Kullanıcı başarıyla güncellendi",
          type: "success",
        });
        closeModal();
        loadData();
      } catch (error) {
        console.error("Error updating user:", error);
        setAlertModal({
          isOpen: true,
          message: "Kullanıcı güncellenirken hata oluştu",
          type: "error",
        });
      } finally {
        setSaving(false);
      }
      return;
    }

    // Yeni kullanıcı
    if (!formData.password || formData.password.trim().length < 6) {
      setAlertModal({
        isOpen: true,
        message: "Şifre en az 6 karakter olmalıdır",
        type: "warning",
      });
      return;
    }
    try {
      setSaving(true);
      const createData = {
        username: formData.email,
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        workshopIds:
          formData.workshopIds.length > 0 ? formData.workshopIds : undefined,
        isActive: true,
        createdBy: currentUser?.userId || "unknown",
      };
      await authService.register(createData);
      setAlertModal({
        isOpen: true,
        message: `Kullanıcı "${formData.firstName} ${formData.lastName}" başarıyla oluşturuldu!`,
        type: "success",
      });
      closeModal();
      loadData();
    } catch (error: any) {
      console.error("Error creating user:", error);
      setAlertModal({
        isOpen: true,
        message:
          "Kullanıcı oluşturulamadı: " +
          (error.response?.data?.message || error.message),
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (user: User) => {
    setConfirmModal({
      isOpen: true,
      userId: user.userId,
      userName: `${user.firstName} ${user.lastName}`,
    });
  };

  const handleConfirmDelete = async () => {
    if (!confirmModal.userId) return;
    try {
      await userService.delete(confirmModal.userId);
      setAlertModal({
        isOpen: true,
        message: `Kullanıcı "${confirmModal.userName}" başarıyla silindi`,
        type: "success",
      });
      setConfirmModal({ isOpen: false, userId: null, userName: "" });
      loadData();
    } catch (error: any) {
      console.error("Error deleting user:", error);
      setAlertModal({
        isOpen: true,
        message:
          "Kullanıcı silinirken hata oluştu: " +
          (error.response?.data?.message || error.message),
        type: "error",
      });
      setConfirmModal({ isOpen: false, userId: null, userName: "" });
    }
  };

  const getRoleLabel = (role: string) =>
    roles.find((r) => r.value === role)?.label || role;

  const totalUsers = users.length;
  const activeUsers = users.filter((u) => u.isActive).length;
  const passiveUsers = totalUsers - activeUsers;
  const workshopRequired =
    formData.role === "Üretim" || formData.role === "Modelist";

  const userColDefs = useMemo(
    () => [
      { key: "name", value: (u: User) => `${u.firstName} ${u.lastName}`.trim() },
      { key: "email", value: (u: User) => u.email || "" },
      { key: "role", value: (u: User) => getRoleLabel(u.role) },
      {
        key: "workshop",
        value: (u: User) => (u.workshops || []).map((w) => w.name).join(", "),
      },
      { key: "status", value: (u: User) => (u.isActive ? "Aktif" : "Pasif") },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const { ctx: userCf, filtered: displayUsers } = useTableColumnFilters(
    users,
    userColDefs,
  );

  if (loading) {
    return (
      <div className="um">
        <div className="um-loading">
          <div className="um-spinner" />
          <span>Yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="um">
      {/* Header */}
      <div className="um-header">
        <div className="um-breadcrumb">
          <span>Yönetim</span>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
          <span className="active">Kullanıcı Yönetimi</span>
        </div>
        <div className="um-header-main">
          <div className="um-header-left">
            <div className="um-header-icon">
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div>
              <h1>Kullanıcı Yönetimi</h1>
              <p>Sistem kullanıcılarını, rollerini ve atölye yetkilerini yönetin</p>
            </div>
          </div>
          <div className="um-header-right">
            <div className="um-stats">
              <div className="um-stat">
                <span className="um-stat-value">{totalUsers}</span>
                <span className="um-stat-label">Toplam</span>
              </div>
              <div className="um-stat">
                <span className="um-stat-value um-ok">{activeUsers}</span>
                <span className="um-stat-label">Aktif</span>
              </div>
              <div className="um-stat">
                <span className="um-stat-value um-muted">{passiveUsers}</span>
                <span className="um-stat-label">Pasif</span>
              </div>
            </div>
            <button onClick={openAdd} className="btn btn-primary">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="8.5" cy="7" r="4" />
                <line x1="20" y1="8" x2="20" y2="14" />
                <line x1="23" y1="11" x2="17" y2="11" />
              </svg>
              Yeni Kullanıcı
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="um-card">
        <div className="um-table-wrap">
          <table className="um-table">
            <thead>
              <tr>
                <FilterTh label="Kullanıcı" colKey="name" ctx={userCf} />
                <FilterTh label="Email" colKey="email" ctx={userCf} />
                <FilterTh label="Rol" colKey="role" ctx={userCf} />
                <FilterTh label="Atölye" colKey="workshop" ctx={userCf} />
                <FilterTh label="Durum" colKey="status" ctx={userCf} />
                <th className="um-th-actions">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {displayUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="um-empty">
                    Kayıt bulunamadı
                  </td>
                </tr>
              ) : (
                displayUsers.map((user) => {
                  const isSelf = user.userId === currentUser?.userId;
                  const tint = roleTints[user.role] || roleTints.Muhasebe;
                  return (
                    <tr
                      key={user.userId}
                      className={!user.isActive ? "um-row-inactive" : ""}
                    >
                      <td>
                        <div className="um-user-cell">
                          <div
                            className="um-avatar"
                            style={{
                              background: pickAvatarColor(
                                user.userId || user.email,
                              ),
                            }}
                          >
                            {initials(user.firstName, user.lastName)}
                          </div>
                          <div className="um-user-meta">
                            <span className="um-user-name">
                              {user.firstName} {user.lastName}
                              {isSelf && <span className="um-you">Siz</span>}
                            </span>
                            <span className="um-user-sub">{user.email}</span>
                          </div>
                        </div>
                      </td>
                      <td className="um-cell-muted">{user.email}</td>
                      <td>
                        <span
                          className="um-role"
                          style={{ background: tint.bg, color: tint.fg }}
                        >
                          {getRoleLabel(user.role)}
                        </span>
                      </td>
                      <td>
                        {user.workshops && user.workshops.length > 0 ? (
                          <div className="um-chips">
                            {user.workshops.map((w) => (
                              <span key={w.workshopId} className="um-chip">
                                {w.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="um-dash">—</span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`um-status ${
                            user.isActive ? "is-active" : "is-inactive"
                          }`}
                        >
                          <span className="um-status-dot" />
                          {user.isActive ? "Aktif" : "Pasif"}
                        </span>
                      </td>
                      <td>
                        <div className="um-actions">
                          <button
                            onClick={() => openEdit(user)}
                            className="btn btn-icon btn-sm btn-secondary"
                            title="Düzenle"
                          >
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(user)}
                            className="btn btn-icon btn-sm btn-soft-danger"
                            disabled={isSelf}
                            title={
                              isSelf
                                ? "Kendi hesabınızı silemezsiniz"
                                : "Kullanıcıyı sil"
                            }
                          >
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              <line x1="10" y1="11" x2="10" y2="17" />
                              <line x1="14" y1="11" x2="14" y2="17" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {isModalOpen && (
        <div className="um-modal-overlay" onMouseDown={closeModal}>
          <div className="um-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="um-modal-head">
              <div className="um-modal-head-left">
                <div className="um-modal-icon">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="8.5" cy="7" r="4" />
                    {!isEdit && <line x1="20" y1="8" x2="20" y2="14" />}
                    {!isEdit && <line x1="23" y1="11" x2="17" y2="11" />}
                  </svg>
                </div>
                <div>
                  <h2>{isEdit ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}</h2>
                  <p>
                    {isEdit
                      ? "Kullanıcı bilgilerini güncelleyin"
                      : "Sisteme yeni bir kullanıcı ekleyin"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="um-modal-close"
                onClick={closeModal}
                aria-label="Kapat"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="um-modal-body" id="um-form">
              <div className="um-section">
                <div className="um-section-title">Kişisel Bilgiler</div>
                <div className="um-form-row">
                  <div className="um-field">
                    <label>Ad <span className="um-req">*</span></label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) =>
                        setFormData({ ...formData, firstName: e.target.value })
                      }
                      placeholder="Ad"
                      required
                    />
                  </div>
                  <div className="um-field">
                    <label>Soyad <span className="um-req">*</span></label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) =>
                        setFormData({ ...formData, lastName: e.target.value })
                      }
                      placeholder="Soyad"
                      required
                    />
                  </div>
                </div>
                <div className="um-field">
                  <label>Email <span className="um-req">*</span></label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder="ornek@softana.com.tr"
                    required
                  />
                </div>
              </div>

              <div className="um-section">
                <div className="um-section-title">Yetki &amp; Atölye</div>
                <div className="um-field">
                  <label>Rol <span className="um-req">*</span></label>
                  <select
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    disabled={isEdit && !isManager}
                    required
                  >
                    <option value="">Rol seçiniz</option>
                    {apiRoles.map((r) => (
                      <option key={r.roleId} value={r.name}>
                        {getRoleLabel(r.name)}
                      </option>
                    ))}
                  </select>
                  {isEdit && !isManager && (
                    <small>Sadece müdür rol değiştirebilir</small>
                  )}
                </div>
                <div className="um-field">
                  <label>
                    Atölyeler {workshopRequired && <span className="um-req">*</span>}
                  </label>
                  {workshops.length === 0 ? (
                    <div className="um-ws-empty">Tanımlı atölye yok</div>
                  ) : (
                    <div className="um-ws">
                      <div className="um-ws-bar">
                        <span className="um-ws-count">
                          {formData.workshopIds.length > 0
                            ? `${formData.workshopIds.length} / ${workshops.length} atölye seçili`
                            : "Atölye seçilmedi"}
                        </span>
                        <button
                          type="button"
                          className="um-ws-selectall"
                          onClick={toggleAllWorkshops}
                        >
                          {formData.workshopIds.length === workshops.length
                            ? "Temizle"
                            : "Tümünü seç"}
                        </button>
                      </div>
                      <div className="um-ws-grid">
                        {workshops.map((workshop) => {
                          const checked = formData.workshopIds.includes(
                            workshop.workshopId,
                          );
                          return (
                            <button
                              type="button"
                              key={workshop.workshopId}
                              className={`um-ws-pill ${checked ? "selected" : ""}`}
                              onClick={() =>
                                toggleWorkshop(workshop.workshopId, !checked)
                              }
                              aria-pressed={checked}
                            >
                              <span className="um-ws-ic" aria-hidden="true">
                                {checked ? (
                                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                ) : (
                                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                    <line x1="12" y1="5" x2="12" y2="19" />
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                  </svg>
                                )}
                              </span>
                              <span className="um-ws-name">{workshop.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {workshopRequired && (
                    <small>Bu rol için en az bir atölye seçimi zorunludur</small>
                  )}
                </div>
              </div>

              <div className="um-section">
                <div className="um-section-title">Güvenlik</div>
                <div className="um-field">
                  <label>
                    {isEdit ? "Yeni Şifre (opsiyonel)" : "Şifre"}{" "}
                    {!isEdit && <span className="um-req">*</span>}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    placeholder={
                      isEdit
                        ? "Değiştirmek istemiyorsanız boş bırakın"
                        : "En az 6 karakter"
                    }
                    required={!isEdit}
                    minLength={isEdit ? undefined : 6}
                  />
                  <small>
                    {isEdit
                      ? "Şifre değiştirmek istemiyorsanız boş bırakın"
                      : "Şifre en az 6 karakter olmalıdır"}
                  </small>
                </div>

                {isEdit && isManager && (
                  <label className="um-toggle">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                    />
                    <span className="um-toggle-track">
                      <span className="um-toggle-thumb" />
                    </span>
                    <span className="um-toggle-text">
                      <strong>Aktif kullanıcı</strong>
                      <small>
                        {formData.isActive
                          ? "Kullanıcı sisteme giriş yapabilir"
                          : "Pasif kullanıcı sisteme giriş yapamaz"}
                      </small>
                    </span>
                  </label>
                )}
              </div>
            </form>

            <div className="um-modal-foot">
              <button
                type="button"
                onClick={closeModal}
                className="btn btn-secondary"
              >
                İptal
              </button>
              <button
                type="submit"
                form="um-form"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving
                  ? "Kaydediliyor..."
                  : isEdit
                    ? "Değişiklikleri Kaydet"
                    : "Kullanıcı Ekle"}
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Kullanıcıyı Sil"
        message={`"${confirmModal.userName}" kullanıcısını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
        type="danger"
        confirmText="Evet, Sil"
        cancelText="İptal"
        onConfirm={handleConfirmDelete}
        onCancel={() =>
          setConfirmModal({ isOpen: false, userId: null, userName: "" })
        }
      />
    </div>
  );
};

export default UserManagement;
